import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const VARIANTS = [
  { src: "/device.webp", name: "Enterprise", spec: "Reference housing" },
  { src: "/device_defense.webp", name: "Defense", spec: "Ruggedized housing" },
  { src: "/device_gold.webp", name: "Field", spec: "Sealed-cover housing" },
  { src: "/device_ring.webp", name: "Home", spec: "Glass-face housing" },
];

const CYCLE_MS = 4500;
const RESUME_MS = 8000;

export const DeviceShowcase = ({ className = "h-64" }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % VARIANTS.length), CYCLE_MS);
    return () => clearInterval(t);
  }, [paused]);

  useEffect(() => () => clearTimeout(resumeTimer.current), []);

  useEffect(() => {
    const onSelect = (e) => {
      setIndex(e.detail);
      setPaused(true);
      clearTimeout(resumeTimer.current);
      resumeTimer.current = setTimeout(() => setPaused(false), RESUME_MS);
    };
    window.addEventListener("vg:select-device", onSelect);
    return () => window.removeEventListener("vg:select-device", onSelect);
  }, []);

  const go = (dir) => {
    setIndex((i) => (i + dir + VARIANTS.length) % VARIANTS.length);
    setPaused(true);
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), RESUME_MS);
  };

  const active = VARIANTS[index];

  return (
    <div
      className={`flex flex-col ${className}`}
      data-testid="device-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="device-wrap relative min-h-0 flex-1">
        <div className="device-glow" aria-hidden="true" />
        <AnimatePresence initial={false}>
          <motion.div
            key={active.src}
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.96, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.02, y: -14 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={active.src}
              alt={`VoiceGuard ${active.name} hardware authentication device`}
              className="h-full w-auto select-none"
              draggable="false"
            />
          </motion.div>
        </AnimatePresence>

        <button
          data-testid="device-prev-button"
          onClick={() => go(-1)}
          aria-label="Previous device"
          className="absolute left-0 top-1/2 -translate-y-1/2 border border-white/10 p-2 text-[#6B7280] transition-colors duration-300 hover:border-[#C4A24C] hover:text-[#C4A24C] sm:-left-8"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          data-testid="device-next-button"
          onClick={() => go(1)}
          aria-label="Next device"
          className="absolute right-0 top-1/2 -translate-y-1/2 border border-white/10 p-2 text-[#6B7280] transition-colors duration-300 hover:border-[#C4A24C] hover:text-[#C4A24C] sm:-right-8"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="mt-6 flex max-w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2">
        <span data-testid="device-counter" className="text-xs tracking-[0.3em] text-[#6B7280] tabular-nums">
          {String(index + 1).padStart(2, "0")} / {String(VARIANTS.length).padStart(2, "0")}
        </span>
        <span className="hidden h-3 w-px bg-white/10 sm:block" aria-hidden="true" />
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={active.name}
            data-testid="device-variant-name"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="font-serif text-xs sm:text-sm tracking-[0.35em] uppercase text-[#C4A24C]"
          >
            {active.name}
          </motion.span>
        </AnimatePresence>
        <span className="hidden h-3 w-px bg-white/10 sm:block" aria-hidden="true" />
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={active.spec}
            data-testid="device-variant-spec"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#6B7280]"
          >
            {active.spec}
          </motion.span>
        </AnimatePresence>
        <span className="hidden h-3 w-px bg-white/10 sm:block" aria-hidden="true" />
        <div className="flex items-center gap-2">
          {VARIANTS.map((v, i) => (
            <button
              key={v.name}
              data-testid={`device-dot-${v.name.toLowerCase()}`}
              onClick={() => go(i - index)}
              aria-label={`Show ${v.name} device`}
              className={`h-1 w-4 transition-colors duration-300 ${i === index ? "bg-[#C4A24C]" : "bg-white/15 hover:bg-white/40"}`}
            />
          ))}
        </div>
      </div>

      <p data-testid="device-shared-core" className="mt-4 text-center text-xs italic text-[#6B7280]">
        Conceptual housings. One breadboard, one PCB — every variant shares the same core.
      </p>
    </div>
  );
};
